/**
 * Seed kiosku PER KARTA — przez postMessage od hosta (pulpit), nie przez localStorage.
 *
 * Dotychczas pulpit zapisywał konfigurację, tożsamość i token do localStorage tuż
 * przed otwarciem kiosku. localStorage jest wspólny dla całego originu, a jeden
 * origin (zks.firmatic.pl:9443) obsługuje DWIE instancje Firmatic — między zapisem
 * a odczytem przez startujący kiosk druga karta mogła seed nadpisać. Migawki per
 * karta (config.service, identity.service) zamykały skutek: żądanie nigdy nie mieszało
 * już instancji W TRAKCIE sesji. Zostawało samo OKNO SEEDU — tu je domykamy.
 *
 * Kanał: kiosk pyta hosta, host odpowiada. Działa w obu osadzeniach:
 *   - iframe pulpitu      -> `window.parent`
 *   - „Otwórz w nowej karcie" -> `window.opener` (pulpit robi window.open BEZ noopener)
 * Brak hosta (wejście z zakładki, instalacja standalone) -> null i stary fallback.
 *
 * Trwałość: odebrany seed ląduje w `sessionStorage`, który jest PER KARTA z definicji.
 * Dzięki temu F5 w karcie, której host już nie żyje (zamknięty pulpit, osierocony
 * opener), nie gubi tożsamości. 🔴 Migawka NIE ma jednak pierwszeństwa przed hostem:
 * gdy host odpowiada, jego seed nadpisuje migawkę — inaczej przełączenie pulpitu
 * na drugą instancję zostawiałoby w karcie klucz i token tej poprzedniej.
 * localStorage zostaje wyłącznie jako ostatnia deska ratunku dla wejść bez hosta.
 *
 * Bezpieczeństwo: seed niesie TOKEN SESYJNY BRAMY, więc obie strony sprawdzają origin,
 * a `postMessage` nigdy nie leci na '*'. Kiosk domyślnie stoi pod `/kiosk/` tego samego
 * originu co pulpit (gw.kioskBase). Gdyby ktoś wystawił kiosk na innym originie,
 * handshake po prostu nie dojdzie do skutku i zostaje dotychczasowy fallback.
 */
import { configService, type BackendConfig } from './config.service';
import { identityService } from './identity.service';

export const SEED_REQUEST = 'firmatic-kiosk-seed-request';
export const SEED_RESPONSE = 'firmatic-kiosk-seed';
const SEED_PROTOCOL = 1;
const TAB_SEED_KEY = 'firmatic-kiosk-seed-tab';

export interface KioskSeed {
  config?: Partial<BackendConfig>;
  identity?: { employeeId?: string | null; partnerId?: string | null };
  ssoToken?: string | null;
  whoamiPath?: string | null;
}

/** Okno, które mogło nas otworzyć: iframe -> parent, nowa karta -> opener. */
function hostWindow(): Window | null {
  try {
    if (window.parent && window.parent !== window) return window.parent;
    if (window.opener && window.opener !== window) return window.opener as Window;
  } catch {
    // cross-origin parent bez dostępu — i tak nie zdołamy się dogadać
  }
  return null;
}

function readTabSeed(): KioskSeed | null {
  try {
    const raw = sessionStorage.getItem(TAB_SEED_KEY);
    return raw ? (JSON.parse(raw) as KioskSeed) : null;
  } catch {
    return null;
  }
}

function writeTabSeed(seed: KioskSeed): void {
  try {
    sessionStorage.setItem(TAB_SEED_KEY, JSON.stringify(seed));
  } catch {
    // brak sessionStorage (tryb prywatny) — seed zadziała, tylko nie przeżyje F5
  }
}

/** Pyta hosta o seed. Rozstrzyga na pierwszej poprawnej odpowiedzi albo po timeoucie. */
function askHost(host: Window, timeoutMs: number): Promise<KioskSeed | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (seed: KioskSeed | null) => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(seed);
    };

    const onMessage = (e: MessageEvent) => {
      // origin MUSI się zgadzać: w drugą stronę poszedł token sesyjny bramy
      if (e.origin !== window.location.origin) return;
      if (e.source !== host) return;
      const d = e.data as { type?: string; v?: number; seed?: KioskSeed } | null;
      if (!d || d.type !== SEED_RESPONSE || d.v !== SEED_PROTOCOL) return;
      finish(d.seed ?? null);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('message', onMessage);
    try {
      host.postMessage({ type: SEED_REQUEST, v: SEED_PROTOCOL }, window.location.origin);
    } catch {
      finish(null);
    }
  });
}

/** Wstrzykuje seed w migawki tej karty (bez dotykania localStorage). */
function apply(seed: KioskSeed): void {
  if (seed.config) configService.applyTabSeed(seed.config);
  if (seed.identity) identityService.set(seed.identity);
}

/**
 * Seed tej karty: host przez postMessage -> sessionStorage (osierocona karta) -> null.
 * Zwraca token i ścieżkę whoami dla ssoBoot; null = brak seedu, stary fallback.
 *
 * 🔴 Kolejność ma znaczenie: pytamy hosta ZAWSZE, gdy host istnieje, a migawki
 * karty używamy dopiero wtedy, gdy hosta już nie ma. Wcześniej było odwrotnie
 * (cache wygrywał) i seed PRZEŻYWAŁ PRZEŁĄCZENIE INSTANCJI: użytkownik otwierał
 * kiosk z pulpitu na preprodzie, przełączał pulpit na produkcję i otwierał kiosk
 * ponownie w TEJ SAMEJ karcie — dostawał klucz API i ścieżkę whoami preprodu.
 * Skutkiem był mieszany stan: adres BFF z nowej instancji (pisany przez pulpit
 * do localStorage), ale klucz i token ze starej → `/api/whoami` 401, brak
 * tożsamości i żądania biegnące do niewłaściwej bazy (ZKS, 2026-08-27).
 *
 * Pierwotny cel migawki jest zachowany: F5 w karcie, której host już nie żyje
 * (zamknięty pulpit, „Otwórz w nowej karcie" po zamknięciu opener), nadal nie
 * gubi tożsamości. Host odpowiada w milisekundach, więc pytanie go za każdym
 * razem nic nie kosztuje.
 */
export async function seedFromHost(timeoutMs = 800): Promise<KioskSeed | null> {
  const host = hostWindow();

  if (host) {
    const seed = await askHost(host, timeoutMs);
    if (seed) {
      apply(seed);
      writeTabSeed(seed);   // nadpisuje migawkę — instancja mogła się zmienić
      return seed;
    }
    // host jest, ale nie odpowiedział (starszy pulpit bez respondera, timeout)
    // — spadamy na migawkę karty, jeśli jakaś została
  }

  const cached = readTabSeed();
  if (cached) {
    apply(cached);
    return cached;
  }
  return null;
}
